/* fbp-slot-guard.js
   ============================================================
   Prevent selecting a multi-slot option that exceeds today's remaining slots.
   - Reads slots left from: .fbp-slots-left (your GemPages slot counter)
   - Infers required slots from selected option digits (2–6) OR data-fbp-required
   - Debounced + signature-guarded to avoid MutationObserver feedback loops
   ============================================================
*/
(() => {
  "use strict";

  const DEBUG = true;

  // Where the counter writes the number
  const SLOTS_LEFT_SELECTOR = ".fbp-slots-left";

  // Optional: you can add a dedicated message container in GemPages:
  // <div class="fbp-slot-guard-msg"></div>
  const MSG_SELECTOR = ".fbp-slot-guard-msg";

  // Add-to-cart button guesses (GemPages + theme)
  const ATC_SELECTORS = [
    'button[type="submit"][name="add"]',
    'button[name="add"]',
    'button[type="submit"]',
    'input[type="submit"][name="add"]',
    ".gp-add-to-cart",              // some GP builds
    "[data-add-to-cart]",           // common pattern
    ".product-form__submit",
  ];

  // If you want to hide a whole GP element when blocked, wrap it with:
  // <div class="fbp-slot-guard"> ... </div>
  const OPTIONAL_CONTAINER_SELECTOR = ".fbp-slot-guard";

  function log(...args) {
    if (!DEBUG) return;
    console.log("[FBP SlotGuard]", ...args);
  }

  function q(sel, root = document) {
    return root.querySelector(sel);
  }
  function qa(sel, root = document) {
    return Array.from(root.querySelectorAll(sel));
  }

  function readSlotsLeft() {
    const el = q(SLOTS_LEFT_SELECTOR);
    if (!el) return null;
    const n = parseInt((el.textContent || "").trim(), 10);
    return Number.isFinite(n) ? n : null;
  }

  function getSelectedVariantId() {
    // Shopify product form convention
    const el = q('form[action^="/cart/add"] [name="id"]');
    const v = el ? (el.value || "").trim() : "";
    return v || null;
  }

  function inferRequiredSlotsFromOptions() {
    // Look for option selects and pick the biggest "count-looking" digit 2-6 in selected values
    const MIN = 2, MAX = 6;
    const sels = qa('select[name^="options["]');
    const nums = [];

    for (const sel of sels) {
      const val = (sel.value || "").trim();
      const m = val.match(/(\d+)/);
      if (!m) continue;
      const n = parseInt(m[1], 10);
      if (Number.isFinite(n) && n >= MIN && n <= MAX) nums.push(n);
    }

    return nums.length ? Math.max(...nums) : 1;
  }

  function getRequiredSlots() {
    // Optional override if you ever set it on the page:
    // <div class="fbp-slot-guard" data-fbp-required="3">
    const guard = q(OPTIONAL_CONTAINER_SELECTOR);
    const attr = guard?.getAttribute?.("data-fbp-required");
    if (attr && Number.isFinite(parseInt(attr, 10))) return parseInt(attr, 10);

    return inferRequiredSlotsFromOptions();
  }

  function findAtcButtons() {
    for (const sel of ATC_SELECTORS) {
      const nodes = qa(sel);
      if (nodes.length) return nodes;
    }
    return [];
  }

  function setDisabled(btn, disabled) {
    if (!btn) return;
    const cur = btn.disabled === true;
    if (disabled === cur) return; // no-op avoids mutation churn
    btn.disabled = !!disabled;
    btn.setAttribute("aria-disabled", disabled ? "true" : "false");
    btn.classList.toggle("fbp-disabled", !!disabled);
  }

  function setMsg(text) {
    let msg = q(MSG_SELECTOR);
    if (!msg) {
      // If no dedicated message element exists, create one near the first ATC button
      const btn = findAtcButtons()[0];
      if (!btn) return;
      msg = document.createElement("div");
      msg.className = "fbp-slot-guard-msg";
      msg.style.marginTop = "10px";
      msg.style.fontSize = "14px";
      msg.style.lineHeight = "1.3";
      msg.style.fontWeight = "600";
      // insert after button
      btn.parentNode?.insertBefore(msg, btn.nextSibling);
    }

    // Avoid rewriting the same message every mutation
    if ((msg.textContent || "") === text) return;
    msg.textContent = text;
  }

  function clearMsg() {
    const msg = q(MSG_SELECTOR);
    if (!msg) return;
    if ((msg.textContent || "") === "") return;
    msg.textContent = "";
  }

  // ---- Signature guard to stop infinite loops ----
  let lastSig = "";
  function sig(slotsLeft, required, variantId) {
    return `${slotsLeft}|${required}|${variantId || ""}`;
  }

  function evaluate(reason = "manual") {
    const slotsLeft = readSlotsLeft();
    const required = getRequiredSlots();
    const variantId = getSelectedVariantId();

    if (slotsLeft == null) {
      // counter not ready yet; do nothing
      return;
    }

    const s = sig(slotsLeft, required, variantId);
    if (s === lastSig) return; // ✅ prevents loop spam
    lastSig = s;

    const ok = required <= slotsLeft;

    log("evaluate", { reason, slotsLeft, required, ok });

    const btns = findAtcButtons();
    if (btns.length) btns.forEach((b) => setDisabled(b, !ok));

    if (!ok) {
      // Your requested wording:
      const max = Math.max(0, slotsLeft);
      setMsg(`Only ${max} slots left today—select ${max} paws (or less).`);
    } else {
      clearMsg();
    }
  }

  // ---- Debounced mutation observer ----
  let t = null;
  function schedule(reason) {
    if (t) clearTimeout(t);
    t = setTimeout(() => evaluate(reason), 80);
  }

  const mo = new MutationObserver(() => schedule("mutation"));
  mo.observe(document.documentElement, { childList: true, subtree: true, attributes: true });

  // Also respond to option changes directly (faster + fewer mutations)
  document.addEventListener("change", (e) => {
    const el = e.target;
    if (el && el.matches && el.matches('select[name^="options["]')) {
      schedule("option_change");
    }
  }, true);

  // Initial runs
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => evaluate("domcontentloaded"));
  } else {
    evaluate("init");
  }

  // Expose for debugging
  window.FBP_SlotGuard = { evaluate };
})();
